import { CurrencyMap, CustomerServiceProvider, Country } from '@/common/config';
import Api from '@/common/api';
import * as PleasedApi from '@/common/api/api.pleased';
import * as Locale from '@/plugins/i18n';
import * as env from '@/common/environment';
import { getBridge } from '@/common/bridge/getBridge';
import { platform } from '@/common/bridge/common';
import {
  FailedKYC,
  InitializeOptions,
  LoginResult,
  PleasedGuestUserInfo,
  PleasedUserInfo,
  SelectedEntity,
} from '@/common/types';
import { ActionContext } from 'vuex';
import { getActivePinia } from 'pinia';
import { useConfig } from '@/stores/useConfig';
import { reportingService } from '@/common/reporting';
import { OpenHelpEntryType } from '@/common/entryLog';
import { analytics } from '@/common/logs/EventLogger';
import { capitalizeFirstLetters, isEmailValid, isPhoneNumber } from '@/common/utils';
import { UserInformation } from '@/common/api/models.pleased';
import { CRITICAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';
import { checkVersionSupported } from '@/common/bot/flow-frameworks/VersionCheck.static';
import { getFallbackUserName } from '@/common/userDisplayName';
import { logger } from '@/common/api/logger';
import { devLog } from '@/common/debug';

export interface GuestFormData {
  name: string;
  email: string;
  phone: string;
  description: string;
}

export interface UserState {
  isLogin: boolean;
  country: Country | string;
  userCountry: string;
  token?: string;
  /**
   * New WAP/WEB auth flag.
   * When enabled, FE will not store SB access token, uses JWE exchange, and proxies SB APIs via host.
   */
  supportJweToken: boolean;
  chatStatus?: string; // null, chat-bot-start,  live-chat
  avatarImgUrl: string;
  phone: string;
  isEmailVerified: boolean;
  provider: CustomerServiceProvider | '';
  directLink: boolean;
  customerAppVersion: string | null;
  supportVoice: boolean;
  // We have a case of login after being guest (with need to know guest data)
  // That's why now we store both
  pleasedUser: PleasedUserInfo;
  guestUser: PleasedGuestUserInfo;
  guestFormData: GuestFormData | null;
  hideLoginButton: boolean;
  name: string;
  /**
   * Email provided from init.
   */
  email: string;
  metaTags: Record<string, string | number | boolean>;
  /**
   * Transaction currency.
   */
  userCurrency: string;
  usernameFieldType: 'phone' | 'email';
  initSelectedEntity?: SelectedEntity | null;
  initFailedKyc?: FailedKYC | null;

  sportyLoginPromise: Promise<unknown> | null;
  greyListed?: boolean;
  /**
   * True while `Bridge.login` is waiting for the parent `LOGIN` response (widget iframe form login).
   * Used to skip duplicate global `onLogin` handling and INIT JWE/token resolution races.
   */
  bridgeWidgetLoginInProgress: boolean;

  pageSource?: OpenHelpEntryType;
  sentEntryLog: boolean;
  capabilities?: {
    supportTwilioVoiceCall?: boolean;
  };
}

interface InitOption {
  token: string;
  country: Country;
  avatarImgUrl: string;
  chatStatus: string;
  phone: string;
  provider: CustomerServiceProvider;
  directLink: boolean | null;
  appVersion: string | null;
  supportVoice: boolean | null;
}

const state: UserState = {
  isLogin: false,
  country: '',
  token: '',
  supportJweToken: false,
  chatStatus: '',
  avatarImgUrl: '',
  phone: '',
  isEmailVerified: false,
  provider: CustomerServiceProvider.PLEASED,
  directLink: false,
  customerAppVersion: null,
  supportVoice: true,
  guestUser: {
    token: '',
    userId: '',
  },
  guestFormData: null,
  pleasedUser: {
    id: '',
    userId: '',
    foreignId: '',
    email: '',
    phone: '',
    token: '',
    name: '',
    displayName: '',
    defaultName: '',
    greyListed: false,
    isGuest: true,
    userLoyaltyTierBand: '',
    initForeignId: '',
  },
  name: '',
  email: '',
  metaTags: {},
  userCurrency: '',
  userCountry: '',
  usernameFieldType: 'phone',
  sportyLoginPromise: null,
  bridgeWidgetLoginInProgress: false,
  hideLoginButton: false,
  greyListed: false,
  initSelectedEntity: null,
  initFailedKyc: null,
  sentEntryLog: false,
  capabilities: undefined,
};

// Check that type/entity is present, including check for fields of entity
// 28.11.2025 Nik – Android version sending empty object for selectedEntity, which resulted in an incident.
export function isValidSelectedEntity(selectedEntity?: SelectedEntity | null): boolean {
  if (!selectedEntity) return true;
  return Boolean(
    selectedEntity && selectedEntity.type && selectedEntity.entity && Object.keys(selectedEntity.entity).length > 0
  );
}

function isValidFailedKYC(failedKYC?: FailedKYC | null): boolean {
  if (!failedKYC) return true;
  return Boolean(failedKYC.show && failedKYC.userRequest);
}

const getters = {
  maskPhone(state: UserState) {
    // user phone 777778888711 -> 77*******711
    if (!state.phone) return '***';
    const length = state.phone.length; // TODO:BUG
    const noMask = [0, 1, length - 1, length - 2, length - 3];
    return (
      state.phone &&
      state.phone.split('').reduce((acc, cur, i) => {
        return noMask.includes(i) ? `${acc}${cur}` : `${acc}*`;
      }, '')
    );
  },
  currency(state: UserState) {
    return state.country ? state.userCurrency || CurrencyMap[state.country] : '';
  },
  maskEmail(state: UserState) {
    if (!state.email) return '***';
    return state.email.replace(/^(.)(.*)(.@.*)$/, (_, a, b, c) => a + b.replace(/./g, '*') + c);
  },
  maskName(state: UserState, getters: Record<string, unknown>) {
    if (state.phone) return getters.maskPhone;
    if (state.email) return getters.maskEmail;
    return '***';
  },
  displayName(state: UserState) {
    return state.name || state.phone || state.email;
  },
  // Should be used instead of state.pleasedUser.name since it will return a capitalized name.
  pleasedUserName(state: UserState) {
    const nameIsEmail = isEmailValid(state.pleasedUser.name);
    const nameIsPhone = isPhoneNumber(state.pleasedUser.name);

    const isNameProvided = state.pleasedUser.name && !nameIsEmail && !nameIsPhone;
    const name = isNameProvided ? capitalizeFirstLetters(state.pleasedUser.name) : '';

    return name;
  },
  // Should be used when pleased name + fallback name is needed.
  pleasedUserDisplayName(_: UserState, getters: Record<string, unknown>) {
    const isNameProvided = !!getters.pleasedUserName;

    const fallbackName = getFallbackUserName();
    const name = isNameProvided ? getters.pleasedUserName : fallbackName;

    return name;
  },
  supportTwilioVoiceCall(state: UserState) {
    const isEnvironmentNativeCallSupported = !!env.get('app')?.nativeCallSupport?.[state.country as Country];
    const isClientNativeCallSupported = !!state.capabilities?.supportTwilioVoiceCall;
    const isAppVersionSupported = checkVersionSupported('nativeCallSupport', false);
    return isEnvironmentNativeCallSupported && isClientNativeCallSupported && isAppVersionSupported;
  },
  supportAbandonedCSAT(state: UserState) {
    const isEnvironmentAbandonedCSATSupported = !!env.get('app')?.abandonedCSATSupport?.[state.country as Country];
    return isEnvironmentAbandonedCSATSupported;
  },
};

const mutations = {
  deleteChatStatus(state: UserState) {
    state.chatStatus = '';
  },
  setIsLogin(state: UserState, isLogin: boolean) {
    state.isLogin = !!isLogin;
  },
  setBridgeWidgetLoginInProgress(state: UserState, value: boolean) {
    state.bridgeWidgetLoginInProgress = !!value;
  },
  // For parents Event
  // INIT
  async initProject(state: UserState, payload: InitializeOptions) {
    try {
      analytics.trackSessionStart({
        sessionParams: {
          language: Locale.i18n.locale,
          platform,
          userAgent: navigator.userAgent,
        },
        metadata: {
          appVersion: payload.appVersion,
          brandCode: payload.brandCode || env.get('brandCode'),
          capabilities: payload.capabilities,
          language: Locale.i18n.locale,
        },
      });

      const rawCountry = payload.sporty?.country || payload.country;
      // Move INT to BR as hotfix since INT moved to BR.
      const country = rawCountry?.toLowerCase() === 'int' ? 'br' : rawCountry;
      Api.setCountry(country as Country);
      state.provider = payload.provider;
      Api.setProvider(payload.provider);

      state.hideLoginButton = !!payload.hideLoginButton;
      // Resolve the configuration by country.
      env.setup(country, payload);

      state.supportJweToken = Boolean(payload.supportJweToken);
      Api.setSupportJweToken(state.supportJweToken);

      if (isValidSelectedEntity(payload.selectedEntity)) {
        state.initSelectedEntity = payload.selectedEntity;
      }

      if (isValidFailedKYC(payload.failedKyc)) {
        state.initFailedKyc = payload.failedKyc;
      }

      state.country = country;
      state.pageSource = payload.pageSource;
      state.pleasedUser.initForeignId = payload.userId || '';
      // state.chatStatus = payload.chatStatus; // currently, Pleased cannot keep the conversation sessions
      state.directLink = !!payload.directLink;
      if (payload.appVersion) {
        state.customerAppVersion = payload.appVersion;
      }
      // android should do the implementation below:
      // 1. AndroidManifest.xml for microphone permission
      // 2, android.webkit.resource.AUDIO_CAPTURE // webrtc request permission
      // 3. webSettings.setMediaPlaybackRequiresUserGesture(false); // without this, apk can only send sound but cannot hear the sound
      if (platform === 'app') {
        state.supportVoice = !!payload.supportVoice;
      } else if (platform === 'ios') {
        // ios webview doesn't support webrtc
        state.supportVoice = false;
      }

      PleasedApi.setConfig({
        // Prefer client configuration.
        url: payload.pleasedUrl,
        brandCode: payload.brandCode || env.get('brandCode'),
      });

      // Sporty access token (legacy). Must not be stored when supportJweToken is enabled.
      const receivedToken = payload.sporty?.token || payload.token;
      const token = state.supportJweToken ? '' : receivedToken || '';
      state.userCurrency = payload.sporty?.currency || '';

      const rawUserCountry = payload.sporty?.userCountry || '';
      // Move INT to BR as hotfix since INT moved to BR.
      state.userCountry = rawUserCountry?.toLowerCase() === 'int' ? 'br' : rawUserCountry;

      if (token) {
        Api.setTokenToHeader(token); // Call before set token in case race condition
        state.token = token;
      } else {
        state.token = '';
      }

      // Legacy flow: SB token presence implies logged-in.
      // JWE flow: we don't know login state at INIT time; login will be set after JWE exchange succeeds.
      const isLogin = !state.supportJweToken && !!token;
      if (isLogin) {
        state.isLogin = true;
        state.phone = payload.phone || '';
        state.email = payload.email || '';
        state.name = payload.name || '';
      }

      state.metaTags = { ...state.metaTags, ...payload.metaTags };

      state.usernameFieldType =
        payload.login?.usernameType || env.get('userFieldType')?.[country as Country] || 'phone';

      if (
        payload.defaultLanguage &&
        Locale.isSupported(payload.defaultLanguage) &&
        env.isLocaleEnabled(payload.defaultLanguage)
      ) {
        Locale.setLanguage(payload.defaultLanguage === ('es-mx' as any) ? 'es-MX' : payload.defaultLanguage);
      }

      state.capabilities = payload.capabilities;

      const pinia = getActivePinia();
      if (pinia) {
        useConfig(pinia).setConfig(payload);
      }
    } catch (e) {
      logger.error('[USER_STORE] Error initializing', { error: parseErrorMessage(e) });
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.CHATBOT_INIT_ERROR,
        metadata: {
          error: parseErrorMessage(e),
          payload,
        },
      });
    }
  },
  updatePleasedUser(state: UserState, payload: UserInformation & { isGuest?: boolean }) {
    const {
      id,
      token,
      phone,
      name = '',
      email = '',
      defaultName,
      isGuest = false,
      userLoyaltyTierBand = '',
      ...rest
    } = payload;

    const displayName = name.trim() || phone || defaultName || '';
    const userId = id;

    state.pleasedUser = {
      id,
      userId,
      token,
      phone,
      name: name.trim(),
      email,
      isGuest,
      displayName,
      defaultName,
      userLoyaltyTierBand,
      initForeignId: state.pleasedUser.initForeignId,
      ...rest,
    };

    PleasedApi.setConfig({ token });
    // Update session with customer id, which will be automatically got from store.
    analytics.updateUserSession({});
  },

  updateGuestUser(state: UserState, payload: UserInformation & { filledDetails?: boolean }) {
    const { id, token, filledDetails = true } = payload;

    state.guestUser = {
      userId: id,
      token,
      filledDetails,
    };
  },

  updateUserGreyListed(state: UserState, payload: { greyListed: boolean }) {
    state.greyListed = payload?.greyListed;
  },
  // LOGIN, LOGIN_POP
  updateLoginStatus(state: UserState, payload: LoginResult) {
    if (payload.token) {
      state.token = payload.token;
      state.isLogin = true;
      state.phone = payload.phone || '';
      state.email = payload.email || '';
      state.name = payload.name || '';
      Api.setTokenToHeader(payload.token);
    }
    state.metaTags = { ...state.metaTags, ...payload.metaTags };
    if (state.avatarImgUrl) {
      state.avatarImgUrl = payload.avatarImgUrl || '';
    }
  },
  updateAvatarImgUrl(state: UserState, url: string) {
    if (url) {
      state.avatarImgUrl = url;
    }
  },
  updateEmailVeryfied(state: UserState, bool: boolean) {
    state.isEmailVerified = bool;
  },
  updateSentEntryLog(state: UserState, bool: boolean) {
    state.sentEntryLog = bool;
  },
  updatePageSource(state: UserState, pageSource: OpenHelpEntryType) {
    state.pageSource = pageSource;
  },
  setSportyPromise(state: UserState, promise: Promise<unknown> | null) {
    state.sportyLoginPromise = promise;
  },

  setUserPhoneOrEmailIfNotExists(state: UserState, payload: Partial<Pick<UserState, 'phone' | 'email' | 'name'>>) {
    if (payload.phone && !(state.phone || '').trim()) state.phone = `${payload.phone}`.trim();
    if (payload.email && !(state.email || '').trim()) state.email = `${payload.email}`.trim();
    if (payload.name && !(state.name || '').trim()) state.name = `${payload.name}`.trim();
  },

  updateSelectedEntity(state: UserState, payload: SelectedEntity | null) {
    if (isValidSelectedEntity(payload)) {
      state.initSelectedEntity = payload;
    }
  },

  // Guest Form Data mutations
  setGuestFormData(state: UserState, payload: GuestFormData) {
    state.guestFormData = payload;
  },
  clearGuestFormData(state: UserState) {
    state.guestFormData = null;
  },
};

const actions = {
  async verifyAuthToken({ state, commit }: ActionContext<UserState, unknown>, payload: { token: string }) {
    try {
      const res = await PleasedApi.getPleasedTokenFromEndUserJweToken({
        countryCode: `${state.country}`,
        jweToken: payload.token,
        userCountry: state.userCountry,
      });

      reportingService.userLoggedIn();
      commit('updatePleasedUser', res);
      commit('updateUserGreyListed', { greyListed: !!res.greyListed });
      commit('setUserPhoneOrEmailIfNotExists', { phone: res.phone, email: res.email, name: res.name });
      // Mark user as logged in after successful exchange.
      commit('setIsLogin', true);
      return res;
    } catch (e) {
      logger.error('[USER_STORE] Error verifying auth token', { error: parseErrorMessage(e) });
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.JWE_TOKEN_EXCHANGE_ERROR,
        metadata: {
          error: parseErrorMessage(e),
        },
      });
      throw e;
    }
  },
  /**
   * New WAP/WEB auth flow:
   * Request JWE from host and exchange it for Pleased token + metadata.
   */
  async ensurePleasedUserToken({ state, dispatch }: ActionContext<UserState, unknown>) {
    if (!(platform === 'wap' || platform === 'web')) return null;
    if (!state.supportJweToken) return null;

    const hasPleasedUser = !!state?.pleasedUser?.token && !state?.pleasedUser?.isGuest;
    if (hasPleasedUser) return null;

    let jweToken = '';

    if (!state?.pleasedUser?.initForeignId) return null;

    try {
      jweToken = await getBridge().getJweToken();
    } catch (e) {
      analytics.trackCriticalEvent({
        name: CRITICAL_EVENTS.GET_JWE_TOKEN_FROM_HOST_ERROR,
        metadata: {
          error: parseErrorMessage(e),
        },
      });
      return null;
    }

    if (!jweToken) return null;

    return await dispatch('verifyAuthToken', { token: jweToken });
  },

  async sportyLoginSuccess({ state, commit, dispatch }: ActionContext<UserState, unknown>, payload: LoginResult) {
    if (state.sportyLoginPromise) {
      await state.sportyLoginPromise;
    }

    const hasPleasedUser = !!state?.pleasedUser?.token && !state?.pleasedUser?.isGuest;

    if ((platform === 'wap' || platform === 'web') && state.supportJweToken) {
      if (getBridge().isLegacyBridge) {
        await dispatch('ensurePleasedUserToken').catch(() => {});
      } else {
        await dispatch('verifyAuthToken', { token: payload.token });
      }
      return;
    }

    if (payload.token !== state.token || !hasPleasedUser) {
      commit('updateLoginStatus', payload);
      const promise = PleasedApi.getPleasedTokenFromSportyToken({
        countryCode: state.country,
        sportyToken: payload.token,
        userCountry: payload.userCountry,
      });
      commit(
        'setSportyPromise',
        promise.then(() => {
          commit('setSportyPromise', null);
        })
      );
      const res = await promise;
      reportingService.userLoggedIn();
      commit('updatePleasedUser', res);
      commit('updateUserGreyListed', {
        greyListed: !!res.greyListed,
      });
    } else {
      devLog.warn('[USER_STORE] Token already registered');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
